import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2, ShieldCheck } from "lucide-react";
import TwoFactorModal from "./TwoFactorModal";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireDashboardAccess?: boolean;
  requireWriteAccess?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  requireAdmin = false,
  requireDashboardAccess = false,
  requireWriteAccess = false
}: ProtectedRouteProps) => {
  const { 
    user, 
    isAdmin,
    canAccessDashboard,
    canWrite,
    loading, 
    needsTwoFactor, 
    checkingTwoFactor, 
    markTwoFactorVerified, 
    clearTwoFactorVerification,
    signOut
  } = useAuth();

  // Écran plein écran pendant le chargement de l'auth ou la vérification 2FA
  if (loading || checkingTwoFactor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 animate-enter">
          <ShieldCheck className="w-10 h-10 text-primary" />
          <p className="text-sm text-muted-foreground text-center">
            Vérification sécurisée de votre accès...
          </p>
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Montrer le 2FA AVANT tout contrôle de rôle admin
  if (needsTwoFactor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <TwoFactorModal
          open={true}
          onSuccess={markTwoFactorVerified}
          onCancel={async () => {
            clearTwoFactorVerification();
            await signOut();
          }}
        />
      </div>
    );
  }

  // Vérifier l'accès dashboard (admin OU viewer)
  if (requireDashboardAccess && !canAccessDashboard) {
    return <Navigate to="/" replace />;
  }

  // Vérifier les droits d'écriture (admin uniquement)
  if (requireWriteAccess && !canWrite) {
    return <Navigate to="/admin" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
